import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import API_BASE_URL from "../utils/apiConfig";

interface ContentItem {
  value: string;
  type: string;
  label: string;
}

interface ContentMap {
  [key: string]: ContentItem;
}

interface GroupedContent {
  [group: string]: ContentMap;
}

interface SiteContentContextType {
  content: GroupedContent;
  loading: boolean;
  getContent: (key: string, fallback?: string) => string;
  refreshContent: () => Promise<void>;
}

const SiteContentContext = createContext<SiteContentContextType>({
  content: {},
  loading: true,
  getContent: () => "",
  refreshContent: async () => {},
});

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<GroupedContent>({});
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/cms/content`);
      if (res.ok) {
        const data = await res.json();
        setContent(data);
      }
    } catch (err) {
      console.error("Failed to load site content:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const getContent = (key: string, fallback: string = ""): string => {
    const parts = key.split(".");
    const group = parts[0];
    if (content[group] && content[group][key]) {
      return content[group][key].value || fallback;
    }
    return fallback;
  };

  return (
    <SiteContentContext.Provider value={{ content, loading, getContent, refreshContent: fetchContent }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
